<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import UrgentOrder from '~/components/modal-windows/UrgentOrder.vue'
import helpers from '~/helpers'
import { useAuthStore } from '~/store/auth'
import { useCartStore } from '~/store/cart'

const emit = defineEmits<{
  (e: 'checkOut'): void
}>()

const authStore = useAuthStore()
const cartStore = useCartStore()

const showAmountWithPromoCode = computed(
  () => !cartStore.isPromoCodeApplied
    && totalWithPromoCodeInKopecks.value !== null
    && totalWithPromoCodeInKopecks.value < cartStore.totalInKopecks
)
const totalWithPromoCodeInKopecks = computed(() => {
  let sum = 0

  for (const { attributes, meta } of cartStore.cartProductsData) {
    const { priceWithPromoCode } = attributes

    if (typeof priceWithPromoCode === 'number') {
      sum += meta.quantity! * (helpers.convertRublesToKopecks(priceWithPromoCode) ?? 0)
    } else {
      return null
    }
  }

  return sum
    + (cartStore.finalShippingCostInKopecks ?? 0)
    + (cartStore.isUrgentOrder && cartStore.urgentOrderPriceInKopecks !== null ? cartStore.urgentOrderPriceInKopecks : 0)
})

async function openUrgentOrderModalWindow (): Promise<void> {
  await useModal({ component: UrgentOrder }).open()
}
</script>

<template>
  <BlockWithBorder>
    <div class="h4">
      Итого
    </div>

    <table class="mb-6">
      <tbody>
        <tr>
          <th>
            Товары ({{ cartStore.totalItems }})

            <ExtraInfo>
              <div v-if="typeof cartStore.weightInGrams === 'number'">
                Примерный вес брутто, г: {{ cartStore.weightInGrams }}
              </div>
            </ExtraInfo>
          </th>
          <td class="price w-px whitespace-nowrap text-right">
            <template v-if="cartStore.discountAmountInKopecks">
              {{ helpers.formatPrice(cartStore.subtotalWithDiscountInKopecks, true) }}&nbsp;₽<br>
              <del>{{ helpers.formatPrice(cartStore.subtotalInKopecks, true) }}&nbsp;₽</del>
            </template>
            <template v-else>
              {{ helpers.formatPrice(cartStore.subtotalWithDiscountInKopecks, true) }}&nbsp;₽
            </template>
          </td>
        </tr>

        <template v-if="authStore.isUser">
          <tr v-if="cartStore.cashbackInPoints !== null && cartStore.cashbackInPoints !== 0">
            <th>
              Баллы iTAB Плюс
            </th>
            <td class="price w-px whitespace-nowrap text-right">
              <Cashback :cashback-in-points="cartStore.cashbackInPoints" :show-units="false" />
            </td>
          </tr>

          <tr v-if="cartStore.cashbackInSuperminsPoints !== null && cartStore.cashbackInSuperminsPoints !== 0">
            <th>
              Баллы Supermins
            </th>
            <td class="price w-px whitespace-nowrap text-right">
              <Cashback :cashback-in-points="cartStore.cashbackInSuperminsPoints" is-supermins :show-units="false" />
            </td>
          </tr>
        </template>

        <tr v-if="cartStore.goldenTicketsCount">
          <th>
            Золотые билеты
          </th>
          <td class="w-px whitespace-nowrap text-right">
            <GoldenTickets :golden-tickets-count="cartStore.goldenTicketsCount" />
          </td>
        </tr>

        <tr v-if="cartStore.backToSchoolTicketsCount">
          <th>
            Билеты #Back2school
          </th>
          <td class="w-px whitespace-nowrap text-right">
            <BackToSchoolTickets :back-to-school-tickets-count="cartStore.backToSchoolTicketsCount" />
          </td>
        </tr>

        <tr v-if="cartStore.discountAmountInKopecks">
          <th>
            Скидка
          </th>
          <td class="discount price w-px whitespace-nowrap text-right">
            −{{ helpers.formatPrice(cartStore.discountAmountInKopecks, true) }}&nbsp;₽
          </td>
        </tr>

        <template v-if="cartStore.shippingCostInKopecks !== null">
          <tr>
            <th>
              Доставка
            </th>
            <td class="price w-px whitespace-nowrap text-right">
              <template v-if="cartStore.isFreeShippingEnabled">
                <template v-if="cartStore.shippingCostInKopecks === 0">
                  0&nbsp;₽
                </template>
                <template v-else>
                  0&nbsp;₽<br>
                  <del>{{ helpers.formatPrice(cartStore.shippingCostInKopecks, true) }}&nbsp;₽</del>
                </template>
              </template>
              <template v-else-if="cartStore.fixedShippingCostInKopecks !== null">
                <template v-if="cartStore.fixedShippingCostInKopecks === cartStore.shippingCostInKopecks">
                  {{ helpers.formatPrice(cartStore.fixedShippingCostInKopecks, true) }}&nbsp;₽
                </template>
                <template v-else>
                  {{ helpers.formatPrice(cartStore.fixedShippingCostInKopecks, true) }}&nbsp;₽<br>
                  <del>{{ helpers.formatPrice(cartStore.shippingCostInKopecks, true) }}&nbsp;₽</del>
                </template>
              </template>
              <template v-else>
                {{ helpers.formatPrice(cartStore.shippingCostInKopecks, true) }}&nbsp;₽
              </template>
            </td>
          </tr>

          <tr v-if="cartStore.isUrgentOrder">
            <th>
              Срочный заказ
            </th>
            <td class="price w-px whitespace-nowrap text-right">
              <div class="inline-flex items-center">
                <span>{{ helpers.formatPrice(cartStore.urgentOrderPriceInKopecks, true) }}&nbsp;₽</span>
                <SvgIcon
                  class="ml-1 text-main-green hover:cursor-pointer hover:text-main-blue"
                  name="outlined/help-outline"
                  @click="openUrgentOrderModalWindow"
                />
              </div>
            </td>
          </tr>

          <tr>
            <th>
              К&nbsp;оплате
            </th>
            <td class="price-total w-px whitespace-nowrap text-right">
              {{ helpers.formatPrice(cartStore.totalInKopecks, true) }}&nbsp;₽
            </td>
          </tr>

          <tr v-if="showAmountWithPromoCode">
            <th>
              К&nbsp;оплате с&nbsp;промокодом
            </th>
            <td class="w-px whitespace-nowrap text-right">
              <AmountWithPromoCode :total-with-promo-code-in-kopecks="totalWithPromoCodeInKopecks as number" />
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <BasicButton color="green" title="Оформить заказ" @click="emit('checkOut')" />
  </BlockWithBorder>
</template>

<style lang="scss" scoped>
@use 'assets/css/price';
</style>
